import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import {
  DollarSign,
  FileText,
  Hash,
  Loader2,
  Package,
  Percent,
  RefreshCw,
  Tag,
  Trash2,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { showError, showSuccess } from "../../../utils/toastUtils";
import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";

const AddProductDrawer = ({
  isOpen,
  onClose,
  onAddProduct,
  editingProduct,
}) => {
  const isEditing = Boolean(editingProduct);

  const [formData, setFormData] = useState({
    productName: "",
    productDescription: "",
    brand: "",
    productUsageType: "",
    mrp: "",
    quantity: "",
    discountPercent: "",
    productImg: "",
    vendor: ""
  });

  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const [vendors, setVendors] = useState([]);


  useEffect(() => {
    if (!isOpen) {
      resetForm();
      return;
    }

    // console.log(editingProduct)

    if (editingProduct) {
      setFormData({
        productName: editingProduct.productName || "",
        productDescription: editingProduct.productDescription || "",
        brand: editingProduct.brand || "",
        productUsageType: editingProduct.productUsageType || "",
        mrp: editingProduct.mrp || "",
        quantity: editingProduct.quantity || "",
        discountPercent: editingProduct.discountPercent || "",
        productImg: editingProduct.productImg || "",
        vendor: editingProduct.vendor || ""
      });
      setImagePreview(editingProduct.productImg || null);
    } else {
      resetForm();
    }
  }, [editingProduct, isOpen]);



  useEffect(() => {
    const fetchVendors = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/vendors`
        );
        setVendors(res.data.data || []);
      } catch (err) {
        showError(err.response?.data.message || "Failed to load vendors");
      }
    };

    if (isOpen) fetchVendors();
  }, [isOpen]);


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImagePreview(URL.createObjectURL(file));
    setFormData((p) => ({ ...p, productImg: file }));
  };

  useEffect(() => {
    const uploadMedia = async () => {
      if (formData.productImg && typeof formData.productImg !== "string") {
        setUploading(true);
        try {
          const { uploadPromise } = uploadFileToCloudinary(formData.productImg);
          const res = await uploadPromise;
          setFormData((p) => ({ ...p, productImg: res.url }));
          setImagePreview(res.url);
          showSuccess("Image uploaded");
        } catch (error) {
          showError("Image upload failed");
        } finally {
          setUploading(false);
        }
      }
    };

    uploadMedia();
  }, [formData.productImg]);

  const handleRemoveImage = () => {
    setImagePreview(null);
    setFormData((p) => ({ ...p, productImg: "" }));
  };

  const validateForm = () => {
    const missing = [];

    if (!formData.productName?.trim()) missing.push("Product Name");
    if (!formData.brand?.trim()) missing.push("Brand");
    if (!formData.productUsageType?.trim()) missing.push("Product Usage Type");
    if (!formData.mrp) missing.push("MRP");
    if (!formData.quantity) missing.push("Quantity");
    if (!formData.vendor) missing.push("Vendor");

    if (missing.length) {
      showError(`Please fill out: ${missing[0]}`);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const payload = {
        productName: formData.productName.trim(),
        productDescription: formData.productDescription.trim(),
        brand: formData.brand.trim(),
        productUsageType: formData.productUsageType.trim(),
        mrp: Number(formData.mrp),
        quantity: formData.quantity ? Number(formData.quantity) : 1,
        discountPercent: formData.discountPercent
          ? Number(formData.discountPercent)
          : 0,
        productImg: formData.productImg,
        vendor: formData.vendor
      };

      const url = isEditing
        ? `${process.env.REACT_APP_API_URL}/products/${editingProduct._id}`
        : `${process.env.REACT_APP_API_URL}/products/create`;

      const method = isEditing ? "put" : "post";

      await axios[method](url, payload);

      showSuccess(isEditing ? "Product updated" : "Product created");
      onAddProduct();
      onClose();
      resetForm();
    } catch (err) {
      console.log(err);
      showError(err?.response?.data?.message || "Operation failed");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      productName: "",
      productDescription: "",
      brand: "",
      productUsageType: "",
      mrp: "",
      quantity: "",
      discountPercent: "",
      productImg: "",
      vendor: ""
    });
    setImagePreview(null);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            className="fixed inset-0 bg-black/50 z-40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={onClose}
          />

          {/* Drawer */}
          <motion.div
            className="fixed top-0 right-0 h-full w-96 md:w-[500px] lg:w-[600px] bg-white z-50 shadow-2xl overflow-hidden"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 30,
            }}
          >
            {/* Header */}
            <div className="flex items-center justify-between p-[15px] border-b bg-brandAccentColor outline-none">
              <h2 className="text-lg font-bold text-primaryTextColor">
                {isEditing ? "Edit Product" : "Add New Product"}
              </h2>

              <button
                type="button"
                onClick={onClose}
                className="rounded-full p-2 transition"
              >
                <X size={26} />
              </button>
            </div>

            {/* Form Body */}
            <form
              onSubmit={handleSubmit}
              className="px-6 pt-6 space-y-6 overflow-y-auto h-[calc(100vh-80px)]"
            >
              {/* Product Name */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  <Package size={16} />
                  Product Name
                </label>
                <input
                  type="text"
                  name="productName"
                  value={formData.productName}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., L'Oreal Hair Serum"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
                />
              </div>

              {/* Brand */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  <Tag size={16} />
                  Brand
                </label>
                <input
                  type="text"
                  name="brand"
                  value={formData.brand}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., L'Oreal, Dove"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
                />
              </div>

              {/* Vendor */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  <Package size={16} />
                  Vendor
                </label>

                <select
                  name="vendor"
                  value={formData.vendor}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg
      focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
                >
                  <option value="" disabled>
                    Select vendor
                  </option>

                  {vendors.map((vendor) => (
                    <option key={vendor._id} value={vendor._id}>
                      {vendor.vendorName} ({vendor.ownerName})
                    </option>
                  ))}
                </select>
              </div>

              {/* Product Usage Type */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  <RefreshCw size={16} />
                  Product Usage Type
                </label>
                <select
                  name="productUsageType"
                  value={formData.productUsageType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
                >
                  <option value="" disabled>
                    Select usage type
                  </option>
                  <option value="use_and_throw">Use & Throw</option>
                  <option value="reusable">Reusable</option>
                </select>
              </div>

              {/* Product Description */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  <FileText size={16} />
                  Description
                </label>
                <textarea
                  name="productDescription"
                  value={formData.productDescription}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Describe the product..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none resize-none"
                />
              </div>

              {/* MRP and Quantity Row */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                    <DollarSign size={16} />
                    MRP
                  </label>
                  <input
                    type="number"
                    name="mrp"
                    value={formData.mrp}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    placeholder="e.g., 499"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
                  />
                </div>

                <div>
                  <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                    <Hash size={16} />
                    Quantity
                  </label>
                  <input
                    type="number"
                    name="quantity"
                    value={formData.quantity}
                    onChange={handleInputChange}
                    min="1"
                    placeholder="e.g., 10"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
                  />
                </div>
              </div>

              {/* Discount Percent */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  <Percent size={16} />
                  Discount Percent
                </label>
                <input
                  type="number"
                  name="discountPercent"
                  value={formData.discountPercent}
                  onChange={handleInputChange}
                  min="0"
                  max="100"
                  placeholder="e.g., 10"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-accentTextColor outline-none"
                />
              </div>

              {/* Image Upload */}
              <div>
                <label className="flex items-center gap-2 text-sm font-bold text-primaryTextColor mb-2">
                  Product Image
                </label>
                <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  {imagePreview ? (
                    <div className="relative inline-block">
                      <img
                        src={imagePreview}
                        alt="Preview"
                        className="mx-auto max-h-48 rounded-lg object-cover"
                      />

                      {uploading && (
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center rounded-lg">
                          <Loader2
                            className="animate-spin text-white"
                            size={32}
                          />
                        </div>
                      )}

                      {!uploading && (
                        <button
                          type="button"
                          onClick={handleRemoveImage}
                          className="mt-3 text-sm text-red-600 hover:underline flex items-center gap-1 mx-auto"
                        >
                          <Trash2 size={16} /> Remove
                        </button>
                      )}
                    </div>
                  ) : (
                    <label className="cursor-pointer block">
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                      <Upload
                        size={32}
                        className="mx-auto mb-3 text-gray-400"
                      />
                      <p className="text-sm text-gray-600">
                        Click to upload image
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG, GIF up to 10MB
                      </p>
                    </label>
                  )}
                </div>
              </div>

              {/* Buttons */}
              <div className="flex gap-4 py-4 border-t sticky bottom-0 bg-white">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={uploading || loading}
                  className="relative flex-1 px-6 py-3 bg-primaryBtnColor text-white font-medium rounded-lg hover:bg-primaryBtnHoverColor transition shadow-md disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {loading || uploading ? (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="animate-spin" size={20} />
                    </span>
                  ) : isEditing ? (
                    "Update Product"
                  ) : (
                    "Add Product"
                  )}
                </button>
              </div>
            </form>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddProductDrawer;